'use client';

import React, { useEffect, useRef, useState } from 'react';
import BookingModal from './PartnershipModal';

export default function BookingSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const els = entry.target.querySelectorAll('.reveal-hidden');
            els.forEach((el, i) => {
              setTimeout(() => {
                el.classList.remove('reveal-hidden');
                el.classList.add('reveal-visible');
              }, i * 140);
            });
          }
        });
      },
      { threshold: 0.15 }
    );
    if (sectionRef?.current) observer?.observe(sectionRef?.current);
    return () => observer?.disconnect();
  }, []);

  return (
    <>
      <section
        id="booking"
        ref={sectionRef}
        className="py-20 sm:py-28 md:py-40 border-t border-parchment/8 relative overflow-hidden"
      >
        {/* Atmospheric background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-charcoal/30 via-transparent to-transparent pointer-events-none" />

        <div className="max-w-8xl mx-auto px-4 sm:px-8 md:px-14 relative z-10">

          {/* Section label */}
          <div className="flex items-center gap-4 sm:gap-6 mb-12 sm:mb-16 md:mb-20 reveal-hidden">
            <span className="text-gold text-[0.62rem] tracking-[0.28em] uppercase font-medium whitespace-nowrap">
              04 — Book a Shoot
            </span>
            <div className="flex-1 h-px bg-parchment/8" />
          </div>

          {/* Asymmetric layout */}
          <div className="grid grid-cols-1 md:grid-cols-asymmetric gap-0">

            {/* Wide column — CTA */}
            <div className="pr-0 md:pr-24 mb-12 md:mb-0">
              <h2
                className="font-serif text-parchment leading-[0.92] tracking-[-0.015em] mb-6 sm:mb-8 reveal-hidden"
                style={{ fontSize: 'clamp(2.2rem, 6vw, 5.5rem)' }}
              >
                Got an event
                <br />
                <span className="italic font-light text-parchment/60">
                  worth capturing?
                </span>
              </h2>

              <p className="text-parchment/45 text-sm sm:text-base font-light leading-[1.8] tracking-wide max-w-lg mb-8 sm:mb-12 reveal-hidden reveal-delay-1">
                Whether it&apos;s a 50-car meet or a 500-car show, a private build
                shoot or a full event recap — if the scene has energy, I want to
                be there with a camera.
              </p>

              <p className="text-parchment/30 text-sm font-light leading-relaxed max-w-sm mb-10 sm:mb-14 reveal-hidden reveal-delay-2">
                Reach out with your event details and I&apos;ll get back to you
                within 24 hours. No shoot is too small if the culture is right.
              </p>

              <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-5 reveal-hidden reveal-delay-3">
                <a href="https://ig.me/m/knxckout.media" target="_blank" rel="noopener noreferrer" className="gold-btn w-full sm:w-auto text-center">
                  <span>Book a Shoot</span>
                </a>
                <p className="text-parchment/25 text-[0.65rem] tracking-wide font-light self-start sm:self-center">
                  Response within 24 hours.
                  <br />
                  All event types welcome.
                </p>
              </div>

              <p className="text-parchment/60 text-sm tracking-wide font-light text-left mt-4 reveal-hidden reveal-delay-3">
                Or, you can Email me at:{' '}
                <a href="https://mail.google.com/mail/?view=cm&to=Knxckoutmedia@gmail.com" target="_blank" rel="noopener noreferrer" className="text-gold hover:text-gold/80 transition-colors">
                  Knxckoutmedia@gmail.com
                </a>
              </p>
            </div>

            {/* Narrow column — what to expect */}
            <div className="md:pl-16 border-t md:border-t-0 md:border-l border-parchment/8 pt-10 md:pt-0 flex flex-col justify-center gap-0 reveal-hidden reveal-delay-1">
              <p className="text-parchment/30 text-[0.62rem] tracking-[0.2em] uppercase mb-6 sm:mb-8">
                What I Shoot
              </p>
              {[
                {
                  type: 'Car Meets & Shows',
                  desc: 'Full event coverage from setup to sendoff. Candid and editorial.',
                },
                {
                  type: 'Rallies & Track Days',
                  desc: 'Motion, speed, and atmosphere. Built for the feed and the archive.',
                },
                {
                  type: 'Build & Detail Shoots',
                  desc: 'Your car deserves a proper portrait. Studio or location.',
                },
                {
                  type: 'Social & Lifestyle',
                  desc: 'Content for your brand, your page, or your personal archive.',
                },
              ]?.map((p, i) => (
                <div
                  key={i}
                  className={`py-5 sm:py-7 ${i < 3 ? 'border-b border-parchment/8' : ''}`}
                >
                  <p className="text-parchment text-sm font-medium tracking-wide mb-1.5">
                    {p?.type}
                  </p>
                  <p className="text-parchment/35 text-xs font-light leading-relaxed tracking-wide">
                    {p?.desc}
                  </p>
                </div>
              ))}
            </div>

          </div>
        </div>
      </section>
      <BookingModal isOpen={modalOpen} onClose={() => setModalOpen(false)} />
    </>
  );
}